/**
 * Serialise work per entity, across tabs.
 *
 * The write seam and the flusher both decide things about an entity and then
 * act on them; running those decisions back to back for one entity, while
 * letting different entities interleave, is what keeps a live write from
 * overtaking a queued one and a replay from acknowledging a row that was
 * replaced under it. With Web Locks the lock is shared by every tab on the
 * origin; without it, an in-memory chain covers this tab.
 *
 * Callers must not invoke anything that takes the same key from inside `fn`
 * (a conflict handler that writes the entity, for instance): both callers in
 * this codebase release the lock before handing off to a handler.
 */

const LOCK_PREFIX = 'pa-offline-entity:';
const tails = new Map<string, Promise<unknown>>();

/** Run `fn` after every earlier `withEntityLock` call for the same key, in this tab, has settled. */
async function withLocalLock<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const previous = tails.get(key) ?? Promise.resolve();
  // `fn` is invoked with no arguments on both lock paths: the resolution
  // value of the previous holder is not this caller's business, and a `fn`
  // that happens to take a parameter must not silently receive it.
  const mine = previous.then(
    () => fn(),
    () => fn()
  );
  const settled = mine.then(
    () => undefined,
    () => undefined
  );
  tails.set(key, settled);
  try {
    return await mine;
  } finally {
    if (tails.get(key) === settled) tails.delete(key);
  }
}

/** Run `fn` holding the entity's lock. */
export async function withEntityLock<T>(key: string, fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function') {
    return (await navigator.locks.request(LOCK_PREFIX + key, () => fn())) as T;
  }
  return withLocalLock(key, fn);
}
